import { FC, useEffect, useRef, useState } from 'react'
import styled from 'styled-components'
import {
  Capitalized,
  ExtraTinyBold,
  MediumTinyBold
} from '@/components/atoms/typography'
import { ID } from 'graphql-ws'
import { Box, Flex } from '@/components/atoms/Grid'
import {
  CloseIconButton,
  ConfirmButtonRedesign,
  SmallBorderedButton
} from '@/components/atoms/button'
import CheckBoxFilterRedesign from '@/components/molecules/checkBoxFilterRedesign'
import useTranslation from 'next-translate/useTranslation'
import { FilterQueryItem } from '@/types/filterQueryItem'
import { SearchIconNewDefault } from '@/components/atoms/icons/SearchIconDefaultNew'
import { ClearIconDefault } from '@/components/atoms/icons/ClearIconDefault'

type DropwDownItemGridItem = {
  id: ID
  title: string
  count: number
  color?: string
  isSelected: boolean
  englishName?: string
  onSelect?: () => void
}

export type DropwDownGridItemSection = {
  title?: string
  items: DropwDownItemGridItem[]
}

type DropDownItemGridProps = {
  sections: DropwDownGridItemSection[]
  typeDropDownItem: FilterQueryItem
  isMultiselect?: boolean
  onDiscard: () => void
  onConfirm: (selectedIds: string[], typeDropDownItem: FilterQueryItem) => void
  onClose: () => void
  withSearch?: boolean
  searchPlaceholder?: string
  searchText?: string
  setSearchText?: (val: string) => void
}

const DropDownGridItem: FC<DropDownItemGridProps> = ({
  sections,
  onDiscard,
  onConfirm,
  onClose,
  typeDropDownItem,
  isMultiselect = true,
  withSearch = false,
  searchPlaceholder,
  searchText,
  setSearchText
}) => {
  const { t } = useTranslation('common')

  const [selectedIds, setSelectedIds] = useState<string[]>([])
  const inputRef = useRef<HTMLInputElement>(null)
  const isSelectionEmpty = selectedIds.length === 0

  useEffect(() => {
    const initial = sections.flatMap((s) =>
      s.items.filter((i) => !!i.isSelected).map((i) => String(i.id))
    )
    setSelectedIds(initial)
  }, [sections])

  const areIdSetsEqual = (a: string[], b: string[]) => {
    if (a.length !== b.length) {
      return false
    }
    const bSet = new Set(b)
    return a.every((x) => bSet.has(x))
  }

  const handleClick = (item: DropwDownItemGridItem) => {
    if (isMultiselect) {
      setSelectedIds((prev) =>
        prev.includes(String(item.id))
          ? prev.filter((x) => x !== String(item.id))
          : [...prev, String(item.id)]
      )
      return
    }

    item.onSelect?.()
  }

  const handleConfirm = () => {
    if (!isMultiselect) {
      onClose?.()
      return
    }

    const originalIds = sections.flatMap((s) =>
      s.items.filter((i) => !!i.isSelected).map((i) => String(i.id))
    )
    const newIds = selectedIds.map(String)

    const changed = !areIdSetsEqual(originalIds, newIds)

    if (changed && onConfirm && typeDropDownItem) {
      onConfirm(newIds, typeDropDownItem)
    }

    onClose?.()
  }

  const handleDiscard = () => {
    const originalIds = sections.flatMap((s) =>
      s.items.filter((i) => !!i.isSelected).map((i) => String(i.id))
    )

    if (originalIds.length > 0) {
      onDiscard()
    } else {
      setSelectedIds([])
    }
  }

  const clearInput = () => {
    setSearchText('')
    inputRef.current?.focus()
  }

  const allItems = sections.flatMap((s) => s.items)

  const headerTitle =
    typeDropDownItem === FilterQueryItem.GENDER
      ? t('Category.Filter.genderTitle')
      : typeDropDownItem === FilterQueryItem.CATEGORY
      ? t('Category.Filter.categoryTitle')
      : typeDropDownItem === FilterQueryItem.SIZE
      ? t('Category.Filter.sizeTitle')
      : typeDropDownItem === FilterQueryItem.COLOR
      ? t('Category.Filter.colorTitle')
      : typeDropDownItem === FilterQueryItem.MATERIAL
      ? t('Category.Filter.materialTitle')
      : typeDropDownItem === FilterQueryItem.MANUFACTURER
      ? t('Category.Filter.brandTitle')
      : typeDropDownItem === FilterQueryItem.PATTERN
      ? t('Category.Filter.motiveTitle')
      : t('Category.Filter.itemTitle')

  return (
    <>
      <StyledFlex>
        <StyledCapitalized>{headerTitle}</StyledCapitalized>
        <CloseIconButton
          onClick={onClose}
          width={10}
          height={10}
          showBackground={false}
        />
      </StyledFlex>

      {withSearch && (
        <SearchWrapper>
          <SearchInputBox>
            <SearchButton
              aria-label="Open search bar"
              type="button"
              onClick={() => inputRef.current?.focus()}
            >
              <SearchIconNewDefault />
            </SearchButton>
            <SearchInput
              ref={inputRef}
              id="dropdownSearchInput"
              type="text"
              name="dropdownSearchInput"
              value={searchText}
              placeholder={searchPlaceholder || t('Hľadať...')}
              onChange={(e) => setSearchText?.(e.target.value)}
              data-testid="dropdownGridSearchInput"
            />
            {searchText && (
              <StyledClearButton type="button" onClick={clearInput}>
                <ClearIconDefault />
              </StyledClearButton>
            )}
          </SearchInputBox>
        </SearchWrapper>
      )}

      <ScrollableContent>
        {allItems.map((item) => (
          <CheckBoxFilterRedesign
            key={item.id}
            size={20}
            isChecked={selectedIds.includes(String(item.id))}
            color={item.color}
            onCheckboxClick={() => handleClick(item)}
          >
            <StyledMediumTinyBold>{item.title}</StyledMediumTinyBold>
            <StyledBox>
              <StyledExtraTinyBold>{item.count}</StyledExtraTinyBold>
            </StyledBox>
          </CheckBoxFilterRedesign>
        ))}
      </ScrollableContent>

      <ButtonsWrapper>
        <ConfirmButtonRedesign
          title={t('Category.Filter.confirmSelection')}
          onClick={handleConfirm}
          data-testid="confirmFilterButton"
          isDisabled={isSelectionEmpty}
        />
        <StyledSmallBorderedButton
          title={t('Category.Filter.delete')}
          onClick={handleDiscard}
          data-testid="cancelFilterButton"
          isDisabled={isSelectionEmpty}
        />
      </ButtonsWrapper>
    </>
  )
}

export default DropDownGridItem

const ScrollableContent = styled(Box)`
  overflow-y: auto;
  max-height: 272px;
  scrollbar-width: thin;
  padding: 4px 0;
`

const ButtonsWrapper = styled(Flex)`
  background: white;
  padding: 8px;
  border-top: 1px solid ${(props) => props.theme.colors.navMenuGrey};
  flex-direction: column;
  gap: 4px;
`

const StyledFlex = styled(Flex)`
  justify-content: space-between;
  align-items: center;
  padding: 6px 12px;
  background-color: ${(props) => props.theme.colors.navMenuGrey};
  gap: 16px;
`

const StyledCapitalized = styled(Capitalized)`
  font-weight: 700;
  font-size: 12px;
  text-transform: uppercase;
  margin: 0;
`

const SearchWrapper = styled.div`
  padding: 8px;
  border-bottom: 1px solid ${(props) => props.theme.colors.navMenuGrey};
`

const StyledMediumTinyBold = styled(MediumTinyBold)`
  font-weight: 500;
  margin: 0 auto 0 0;
  font-size: 14px;
`

const StyledExtraTinyBold = styled(ExtraTinyBold)`
  font-weight: 400;
  margin: 0;
  font-size: 12px;
`

const StyledBox = styled(Box)`
  padding: 4px;
  border-radius: 4px;
  background-color: ${(props) => props.theme.colors.navMenuGrey};
`

const StyledSmallBorderedButton = styled(SmallBorderedButton)`
  padding: 8px 16px;
  box-sizing: border-box;
  border-color: ${(props) => props.theme.colors.buttonBorderNew};
  height: 33px;
  border-radius: 4px;
  opacity: ${(props) => (props.isDisabled ? 0.5 : 1)};
  pointer-events: ${(props) => (props.isDisabled ? 'none' : 'auto')};
  cursor: ${(props) => (props.isDisabled ? 'not-allowed' : 'pointer')};
  p {
    margin: 0;
    font-size: 14px;
  }
`

const SearchInputBox = styled(Flex)`
  max-width: 100%;
  position: relative;
`

const StyledClearButton = styled.div`
  border-radius: 50%;
  background: none;
  display: flex;
  align-items: center;
  justify-content: center;
  position: absolute;
  right: 8px;
  top: 50%;
  transform: translateY(-50%);
  z-index: 1;

  &:hover {
    cursor: pointer;
  }

  & svg {
    width: 14px;
    height: 14px;
  }
`

const SearchInput = styled.input`
  flex: 1;
  border: none;
  border-radius: 4px;
  background: ${(props) => props.theme.colors.navMenuGrey};
  outline: none;
  padding: 5px 30px;
  font-size: 14px;
  box-sizing: border-box;
  height: 29px;

  &::placeholder {
    color: #757575;
  }
  &::-ms-input-placeholder {
    color: ${(props) => props.theme.colors.searchInputColor};
  }
  &:focus {
    outline-style: none;
    border: ${(props) => props.isSearchbarOpen && 'none'};
  }
`

const SearchButton = styled.button`
  background: none;
  border: none;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 24px;
  height: 24px;
  position: absolute;
  left: 1px;
  top: 50%;
  transform: translateY(-50%);
  z-index: 1;

  & svg {
    fill: ${(props) => props.theme.colors.searchButton};
    width: 14px;
    height: auto;
  }
  &:hover svg {
    fill: ${(props) => props.theme.colors.searchInputColor};
  }
`
